// frontend/src/utils/localeOptions.js
//
// Shared option lists for the three locale axes (language, region,
// timezone), so every form that edits them offers the same lists.
//
// languageOptions — the languages the app is translated into (APP_LANGUAGES;
//   the region is collected separately).
// regionOptions — ISO 3166-1 alpha-2 codes (what Intl.* and Shopify's
//   shopAddress.countryCodeV2 emit); `current` adds the stored value when the
//   curated list lacks it (a Shopify shop's country can be any), so the
//   select can show it.
// Both label each code with its name in the merchant's UI language
//   (Intl.DisplayNames). `storeDefault` adds the empty-string entry a user's
//   own setting uses to fall back to the store's.
// COMMON_TIMEZONES — IANA timezone names. Curated subset of pytz's
//   common_timezones for autocomplete; users can also type any IANA name
//   (validated server-side).

import { APP_LANGUAGES } from '../i18n/languages';

const REGION_CODES = [
  'US', 'GB', 'AU', 'AT', 'BE', 'BR', 'CA', 'CN', 'CZ', 'DK', 'FI', 'FR', 'DE',
  'GR', 'HK', 'IN', 'ID', 'IE', 'IL', 'IT', 'JP', 'MY', 'MX', 'NL', 'NZ', 'NO',
  'PL', 'PT', 'RO', 'SG', 'ZA', 'KR', 'ES', 'SE', 'CH', 'TH', 'TR', 'AE',
];

function options(codes, type, { t, displayLanguage, storeDefault, current }) {
  const names = new Intl.DisplayNames(displayLanguage, { type });
  const listed = current && !codes.includes(current) ? [...codes, current] : codes;
  const named = listed
    .map((code) => ({ value: code, label: names.of(code) }))
    .sort((a, b) => a.label.localeCompare(b.label, displayLanguage));
  return storeDefault
    ? [{ value: '', label: t('userProfile.useStoreDefault') }, ...named]
    : named;
}

/** `{ t, i18n }` from useTranslation; options as in the header comment. */
export function languageOptions({ t, i18n }, { storeDefault = false } = {}) {
  return options(APP_LANGUAGES, 'language', {
    t, displayLanguage: i18n?.resolvedLanguage, storeDefault,
  });
}

export function regionOptions({ t, i18n }, { storeDefault = false, current = '' } = {}) {
  return options(REGION_CODES, 'region', {
    t, displayLanguage: i18n?.resolvedLanguage, storeDefault, current,
  });
}

export const COMMON_TIMEZONES = [
  'UTC',
  'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam',
  'Europe/Madrid', 'Europe/Rome', 'Europe/Warsaw', 'Europe/Brussels',
  'Europe/Zurich', 'Europe/Vienna', 'Europe/Prague', 'Europe/Stockholm',
  'Europe/Oslo', 'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Dublin',
  'Europe/Lisbon', 'Europe/Athens', 'Europe/Bucharest', 'Europe/Moscow',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore',
  'Asia/Seoul', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
  'America/Toronto', 'America/Vancouver', 'America/Mexico_City',
  'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'Africa/Johannesburg', 'Africa/Cairo', 'Africa/Lagos',
];
